/**
 * Web Form hooks using TanStack Query
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { webFormService } from '@/api'
import { useUIStore } from '@/stores/uiStore'
import { handleApiError } from '@/lib/apiErrorHandler'
import { leadKeys } from './useLeads'
import type {
  LeadWebListResponse,
  LeadWebFilterParams,
} from '@/types/api'

/**
 * Query keys for web forms
 */
export const webFormKeys = {
  all: ['webForms'] as const,
  lists: () => [...webFormKeys.all, 'list'] as const,
  list: (filters: LeadWebFilterParams) => [...webFormKeys.lists(), filters] as const,
}

/**
 * Hook to get paginated web form leads
 */
export function useWebFormLeads(params?: LeadWebFilterParams) {
  return useQuery<LeadWebListResponse>({
    queryKey: webFormKeys.list(params || {}),
    queryFn: () => webFormService.getWebFormLeads(params),
  })
}

/**
 * Hook for marking a web form submission as converted after a manual merge
 */
export function useMarkWebFormConverted() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, convertedLeadId }: { id: string; convertedLeadId: string }) =>
      webFormService.markConverted(id, convertedLeadId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: webFormKeys.lists() })
    },
  })
}

/**
 * Hook for converting a web form to a lead
 */
export function useConvertToLead() {
  const queryClient = useQueryClient()
  const { addToast } = useUIStore()

  return useMutation({
    mutationFn: ({ id, assignedTo }: { id: string; assignedTo: string }) =>
      webFormService.convertToLead(id, { assigned_to: assignedTo }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: webFormKeys.lists() })
      queryClient.invalidateQueries({ queryKey: leadKeys.lists() })
      addToast({
        title: 'Lead created',
        description: data.message,
        variant: 'success',
      })
    },
    onError: (error: { detail?: string }) => {
      handleApiError(error, 'Failed to convert form submission')
    },
  })
}
